import {
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  parse,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks
} from 'date-fns';

export type AttendanceDashboardPeriodPreset =
  | 'date'
  | 'last-week'
  | 'last-month'
  | 'range';

export interface AttendanceDashboardPeriod {
  preset: AttendanceDashboardPeriodPreset;
  from: Date;
  to: Date;
}

const WEEK_OPTS = { weekStartsOn: 1 as const };

export const getLastWeekPeriod = (reference = new Date()): AttendanceDashboardPeriod => {
  const lastWeek = subWeeks(reference, 1);

  return {
    preset: 'last-week',
    from: startOfWeek(lastWeek, WEEK_OPTS),
    to: endOfWeek(lastWeek, WEEK_OPTS)
  };
};

export const getLastMonthPeriod = (reference = new Date()): AttendanceDashboardPeriod => {
  const lastMonth = subMonths(reference, 1);

  return {
    preset: 'last-month',
    from: startOfMonth(lastMonth),
    to: endOfMonth(lastMonth)
  };
};

export const createDatePeriod = (date: Date): AttendanceDashboardPeriod => ({
  preset: 'date',
  from: date,
  to: date
});

export const createRangePeriod = (from: Date, to: Date): AttendanceDashboardPeriod => ({
  preset: 'range',
  from,
  to: to < from ? from : to
});

export const formatDashboardPeriodPresetName = (
  preset: AttendanceDashboardPeriodPreset
) => {
  switch (preset) {
    case 'date':
      return 'Date';
    case 'last-week':
      return 'Last week';
    case 'last-month':
      return 'Last month';
    case 'range':
      return 'Range';
    default:
      return 'Last week';
  }
};

export const formatDashboardPeriodRange = (period: AttendanceDashboardPeriod) =>
  period.preset === 'date'
    ? format(period.from, 'EEE, d MMM yyyy')
    : `${format(period.from, 'd MMM yyyy')} — ${format(period.to, 'd MMM yyyy')}`;

export const formatDashboardPeriodLabel = (period: AttendanceDashboardPeriod) => {
  const range = formatDashboardPeriodRange(period);
  const presetName = formatDashboardPeriodPresetName(period.preset);
  return `${presetName} · ${range}`;
};

export const formatDashboardPeriodQuery = (period: AttendanceDashboardPeriod) => ({
  from: format(period.from, 'yyyy-MM-dd'),
  to: format(period.to, 'yyyy-MM-dd')
});

export const periodToSearchParams = (
  period: AttendanceDashboardPeriod
): URLSearchParams => {
  const { from, to } = formatDashboardPeriodQuery(period);

  return new URLSearchParams({
    from,
    to,
    preset: period.preset
  });
};

export const parsePeriodFromSearchParams = (
  searchParams: URLSearchParams,
  fallback: AttendanceDashboardPeriod
): AttendanceDashboardPeriod => {
  const fromStr = searchParams.get('from');
  const toStr = searchParams.get('to');
  const presetParam = searchParams.get('preset');

  if (!fromStr || !toStr) return fallback;

  const from = parse(fromStr, 'yyyy-MM-dd', new Date());
  const to = parse(toStr, 'yyyy-MM-dd', new Date());

  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return fallback;

  const preset: AttendanceDashboardPeriodPreset =
    presetParam === 'date' ||
    presetParam === 'last-week' ||
    presetParam === 'last-month' ||
    presetParam === 'range'
      ? presetParam
      : 'range';

  return { preset, from, to: to < from ? from : to };
};

export const buildEmployeesListPath = (
  period?: AttendanceDashboardPeriod
): string => {
  if (!period) return '/attendance-tracking/employees';

  return `/attendance-tracking/employees?${periodToSearchParams(period).toString()}`;
};

export const buildEmployeeDetailPath = (
  employeeId: string,
  period: AttendanceDashboardPeriod
): string =>
  `/attendance-tracking/employees/${encodeURIComponent(employeeId)}?${periodToSearchParams(period).toString()}`;

export const buildEmployeeCalendarPath = (
  employeeId: string,
  period?: AttendanceDashboardPeriod
): string => {
  const base = `/attendance-tracking/employees/${encodeURIComponent(employeeId)}/calendar`;
  if (!period) return base;

  return `${base}?${periodToSearchParams(period).toString()}`;
};

export const buildAttendanceExportFileName = (from: string, to: string) => {
  const fromDate = parse(from, 'yyyy-MM-dd', new Date());
  const toDate = parse(to, 'yyyy-MM-dd', new Date());

  return `attendance-export ${format(fromDate, 'd MMM yyyy')} - ${format(toDate, 'd MMM yyyy')}.xlsx`;
};

/** Below-compliance KPI only applies to a full calendar month. */
export const isFullMonthDashboardPeriod = (
  period: AttendanceDashboardPeriod
): boolean => {
  if (period.preset === 'last-month') return true;
  if (period.preset === 'last-week') return false;

  const monthStart = startOfMonth(period.from);
  const monthEnd = endOfMonth(period.from);

  return (
    period.from.getFullYear() === period.to.getFullYear() &&
    period.from.getMonth() === period.to.getMonth() &&
    isSameDay(period.from, monthStart) &&
    isSameDay(period.to, monthEnd)
  );
};
